import React, { Component } from 'react';
import { Link } from 'react-router-dom';

/**
 * TERMS OF SERVICE – GUIDR
 * Last Updated: 2025‑05‑15
 *
 * NOTE: These terms intentionally provide broad protections for Guidr, Inc. while
 * remaining user‑friendly and compliant with current U.S. consumer‑protection
 * laws. They include additional clauses covering DMCA takedowns, force majeure,
 * beta features, export controls, ADA accessibility, electronic communications,
 * class‑action waiver, and a one‑year limitation period on claims. Content is
 * organised in numbered sections for ease of reference and mirrors the visual
 * hierarchy used by other legal pages on the site.
 */

export default class Terms extends Component {
    render() {
        return (
            <div className="content-container">
                <div className="content-wrapper content-wrapper--vertical-padding">

                    {/* ---------- HEADER ---------- */}
                    <div className="legal__title text-title">Terms of Service</div>
                    <div className="legal__textbox text-color-grey">Last Updated: 15 May 2025</div>

                    {/* ---------- 1. ACCEPTANCE ---------- */}
                    <div className="legal__title text-subtitle">1. Acceptance of These Terms</div>
                    <div className="legal__textbox">
                        By accessing or using <strong>guidr.net</strong>, associated mobile or desktop apps,
                        APIs, or other services (collectively, the “<strong>Platform</strong>”), you agree to be
                        bound by these Terms of Service (the “<strong>Terms</strong>”). If you do not agree, do not
                        access or use the Platform.
                    </div>

                    {/* ---------- 2. DEFINITIONS ---------- */}
                    <div className="legal__title text-subtitle">2. Definitions</div>
                    <div className="legal__textbox">
                        “<strong>Creator</strong>” means a user who uploads or publishes itineraries or other
                        content. “<strong>Traveler</strong>” means a user who browses, unlocks, or purchases
                        itineraries. “<strong>Partner</strong>” means hotels, DMOs, guides, or other hospitality
                        brands that create branded content. Unless context requires otherwise,
                        “<strong>you</strong>” or “<strong>User</strong>” refers to any person accessing the Platform.
                    </div>

                    {/* ---------- 3. ELIGIBILITY ---------- */}
                    <div className="legal__title text-subtitle">3. Eligibility</div>
                    <div className="legal__textbox">
                        You must be at least 18 years old and capable of forming a legally binding
                        contract (or the age of majority in your jurisdiction, if higher). You
                        represent and warrant that you meet these requirements and that the
                        information you provide is accurate and complete.
                    </div>

                    {/* ---------- 4. ACCOUNT SECURITY ---------- */}
                    <div className="legal__title text-subtitle">4. Account Registration&nbsp;&&nbsp;Security</div>
                    <div className="legal__textbox">
                        You are responsible for maintaining the confidentiality of your login
                        credentials and all activity under your account. Notify us
                        immediately at <a href="mailto:info@guidr.net">info@guidr.net</a> of any suspected
                        unauthorised use. We may require multi‑factor authentication and may
                        suspend access pending investigation.
                    </div>

                    <div className="legal__title text-subtitle">
                        4A. Data Security & Breach Notification (NY SHIELD Act)
                    </div>
                    <div className="legal__textbox">
                        Guidr maintains a written information-security program that meets or
                        exceeds the administrative, technical, and physical safeguards required
                        by New York General Business Law §§ 899-aa & 899-bb (“SHIELD Act”).
                        In the event we discover unauthorized acquisition of “private
                        information,” we will provide the notifications required by § 899-aa
                        within thirty&nbsp;(30) days of discovery, including notice to affected
                        New York residents and to the NY Attorney General, NY State Police,
                        NY Department of State’s Division of Consumer Protection, and, where
                        applicable, the NY Department of Financial Services.
                    </div>

                    {/* ---------- 5. USER CONTENT &amp; LICENCE ---------- */}
                    <div className="legal__title text-subtitle">5. User‑Generated Content</div>
                    <div className="legal__textbox">
                        <p>You retain ownership of itineraries, images, text, video, or other
                            materials you upload (collectively, “<strong>Content</strong>”). By submitting
                            Content you grant Guidr an irrevocable, worldwide, perpetual, royalty‑free,
                            transferable, and sublicensable licence to host, copy, adapt, publish,
                            distribute, translate, publicly display, create derivative works of, and
                            otherwise use the Content for operating, marketing, and improving the
                            Platform.</p>
                        <p>You represent that you have all rights necessary to grant this licence and
                            that your Content does not infringe any third‑party right or violate any
                            law.</p>
                    </div>

                    {/* ---------- 6. DMCA &amp; IP TAKEDOWN ---------- */}
                    <div className="legal__title text-subtitle">6. DMCA &amp; IP Takedown Policy</div>
                    <div className="legal__textbox">
                        If you believe material on the Platform infringes your copyright, send a
                        compliant notice to our Designated Agent at <a href="mailto:info@guidr.net">info@guidr.net</a>.
                        We respond to valid Digital Millennium Copyright Act (17 U.S.C. §512) and
                        analogous international claims and may remove or disable access to allegedly
                        infringing content.
                    </div>

                    {/* ---------- 7. PROHIBITED CONDUCT ---------- */}
                    <div className="legal__title text-subtitle">7. Acceptable Use</div>
                    <div className="legal__textbox">
                        You agree not to: (a) violate any law; (b) infringe intellectual‑property or
                        privacy rights; (c) publish misleading, defamatory, obscene, or hateful
                        content; (d) transmit malware or unsolicited promotions; (e) interfere with
                        the Platform’s security or performance; (f) scrape or harvest data without
                        written permission; or (g) circumvent payment flows. We may suspend or
                        terminate accounts that breach this section or take legal action.
                    </div>

                    {/* ---------- 8. PAYMENTS &amp; REFUNDS ---------- */}
                    <div className="legal__title text-subtitle">8. Payments, Fees, Refunds &amp; Charge‑backs</div>
                    <div className="legal__textbox">
                        <p>
                            Creators may set prices for their itineraries. Guidr processes payments
                            via third-party processors; creator earnings are remitted net of Guidr
                            service fees, processor fees, charge-backs, applicable taxes, and other
                            fees on the schedule shown in your creator dashboard.
                            <br /><br />
                            <b>Credit-card pricing.</b> If we impose a credit-card surcharge,
                            it will never exceed our direct processor cost and the total price
                            (inclusive of any surcharge) will be displayed to you prior to purchase,
                            per N.Y. GBL § 518.
                            <br /><br />
                            <b>Auto-renewal / subscriptions.</b> Where Guidr offers any automatic-renewal
                            or continuous-service plan, the renewal terms, cancellation method, and
                            minimum purchase obligations will be presented in a clear, conspicuous,
                            and bold-faced manner and may be cancelled online at any time, in
                            compliance with N.Y. GBL § 527-a.
                        </p>
                    </div>

                    <div className="legal__title text-subtitle">
                        8A. Freelance Creator Payments (NY GBL Art. 44-A)
                    </div>
                    <div className="legal__textbox">
                        Each Creator acting as a freelance worker under New York law will
                        receive a written contract specifying compensation and payment date.
                        Guidr will pay such compensation no later than thirty&nbsp;(30) days
                        after the itinerary is accepted or sale proceeds become due, in
                        accordance with the New York “Freelance Isn’t Free Act.”
                    </div>

                    {/* ---------- 9. TRAVEL &amp; ACTIVITY DISCLAIMERS ---------- */}
                    <div className="legal__title text-subtitle">9. Travel &&nbsp;Activity Disclaimers</div>
                    <div className="legal__textbox">
                        Guidr is not a travel agency, tour operator, or insurer. Itineraries are
                        for <em>informational</em> purposes only. We make no guarantee that suggested
                        activities, venues, or transportation will be available, safe, or suitable
                        for your needs. You are solely responsible for verifying local
                        conditions, health requirements, visas, insurance, and personal safety
                        before travel.
                    </div>

                    {/* ---------- 10. THIRD‑PARTY SERVICES ---------- */}
                    <div className="legal__title text-subtitle">10. Third‑Party Services &amp; Links</div>
                    <div className="legal__textbox">
                        The Platform may link to or integrate with third‑party sites and booking
                        services that Guidr does not control. Guidr is not a party to transactions
                        with third parties and disclaims liability for their acts, omissions, or
                        policies.
                    </div>

                    {/* ---------- 11. BETA FEATURES ---------- */}
                    <div className="legal__title text-subtitle">11. Beta &amp; Experimental Features</div>
                    <div className="legal__textbox">
                        We may offer features labelled “beta,” “preview,” or similar. Such
                        features are provided “as is,” may be discontinued at any time, and are
                        excluded from any service‑level or support commitments.
                    </div>

                    {/* ---------- 12. INTELLECTUAL PROPERTY ---------- */}
                    <div className="legal__title text-subtitle">12. Guidr Intellectual Property</div>
                    <div className="legal__textbox">
                        Except for User Content, the Platform - including software, graphics,
                        trademarks, and databases - is owned by Guidr or its licensors. No rights
                        are granted except as explicitly stated in these Terms.
                    </div>

                    {/* ---------- 13. PRIVACY ---------- */}
                    <div className="legal__title text-subtitle">13. Privacy</div>
                    <div className="legal__textbox">
                        Your use of the Platform is subject to our <Link to="/privacy">Privacy Policy</Link>,
                        which explains how we collect, use, and share personal data.
                    </div>

                    {/* ---------- 14. SECURITY ---------- */}
                    <div className="legal__title text-subtitle">14. Security</div>
                    <div className="legal__textbox">
                        We employ reasonable technical and organisational safeguards but cannot
                        guarantee absolute security. You acknowledge that transmission over the
                        internet is inherently risky and that you provide information at your own
                        risk.
                    </div>

                    {/* ---------- 15. WARRANTY DISCLAIMER ---------- */}
                    <div className="legal__title text-subtitle">15. Disclaimers</div>
                    <div className="legal__textbox">
                        The Platform and all content are provided “<strong>as is</strong>” and “<strong>as
                            available</strong>.” Guidr disclaims all warranties, express or implied,
                        including merchantability, fitness for a particular purpose, title, and
                        non‑infringement. No advice or information creates any warranty not
                        expressly stated herein.
                    </div>

                    {/* ---------- 16. LIMITATION OF LIABILITY ---------- */}
                    <div className="legal__title text-subtitle">16. Limitation of Liability</div>
                    <div className="legal__textbox">
                        To the fullest extent permitted by law, Guidr shall not be liable for any
                        indirect, incidental, consequential, special, punitive, or exemplary
                        damages, or loss of profits, data, or goodwill. Guidr’s total aggregate
                        liability for any claim shall not exceed (a) USD 100 or (b) the amount you
                        paid to Guidr in the 12 months preceding the claim, whichever is greater.
                    </div>

                    {/* ---------- 17. INDEMNIFICATION ---------- */}
                    <div className="legal__title text-subtitle">17. Indemnification</div>
                    <div className="legal__textbox">
                        You agree to indemnify, defend, and hold harmless Guidr, its affiliates,
                        and their officers, directors, employees, and agents from any claims or
                        losses arising from your Content, breach of these Terms, or misuse of the
                        Platform.
                    </div>

                    {/* ---------- 18. FORCE MAJEURE ---------- */}
                    <div className="legal__title text-subtitle">18. Force Majeure</div>
                    <div className="legal__textbox">
                        Guidr is not liable for delays or failures resulting from events beyond its
                        reasonable control, including natural disasters, war, terrorism, labour
                        disputes, governmental action, or internet outages.
                    </div>

                    {/* ---------- 19. EXPORT CONTROLS ---------- */}
                    <div className="legal__title text-subtitle">19. Export &&nbsp;Sanctions Compliance</div>
                    <div className="legal__textbox">
                        You may not use, export, or re‑export the Platform or any content except as
                        authorised by United States law and the laws of the jurisdiction in which
                        the Platform was obtained. You represent you are not located in, under
                        control of, or a national or resident of any embargoed country and are not
                        on any U.S. government denied‑party list.
                    </div>

                    {/* ---------- 20. ELECTRONIC COMMUNICATIONS ---------- */}
                    <div className="legal__title text-subtitle">20. Electronic Communications</div>
                    <div className="legal__textbox">
                        You consent to receive disclosures and communications electronically and
                        agree that such communications satisfy any legal requirement that they be
                        in writing.
                    </div>

                    {/* ---------- 21. ACCESSIBILITY ---------- */}
                    <div className="legal__title text-subtitle">21. Accessibility</div>
                    <div className="legal__textbox">
                        Guidr aims to meet the Web Content Accessibility Guidelines (WCAG) 2.1
                        Level AA and continuously improves usability for all visitors.
                        Feedback? Email <a href="mailto:info@guidr.net">
                            info@guidr.net</a>.
                    </div>

                    {/* ---------- 22. CHANGES TO PLATFORM ---------- */}
                    <div className="legal__title text-subtitle">22. Changes to the Platform</div>
                    <div className="legal__textbox">
                        We may add, modify, or discontinue features at any time. Where required
                        by law, we will provide reasonable notice.
                    </div>

                    {/* ---------- 23. DISPUTE RESOLUTION ---------- */}

                    <div className="legal__title text-subtitle">23. Dispute Resolution &&nbsp;Governing Law</div>

                    <div className="legal__textbox text-bold text-color-dark">
                        IMPORTANT: THIS SECTION CONTAINS A BINDING ARBITRATION CLAUSE AND
                        CLASS-ACTION WAIVER THAT AFFECTS YOUR RIGHTS.
                    </div>

                    <div className="legal__textbox">
                        <p><strong>Arbitration &amp; Class‑Action Waiver.</strong> Except for claims that qualify for
                            small‑claims court, any dispute arising out of these Terms shall be
                            resolved by binding individual arbitration administered by the American
                            Arbitration Association under its Commercial Rules. <em>No class or
                                representative proceedings are permitted.</em></p>
                        <p><strong>Opt‑Out.</strong> You may opt out of arbitration by emailing
                            <a href="mailto:info@guidr.net">info@guidr.net</a> within 30 days of first agreeing
                            to these Terms.</p>
                        <p><strong>One‑Year Limitation.</strong> Any claim must be filed within one (1) year after
                            the cause of action accrues or it is permanently barred.</p>
                        <p><strong>Governing Law.</strong> These Terms are governed by the laws of the State of
                            New York, excluding its conflict‑of‑laws principles. If the arbitration
                            clause is unenforceable, the state and federal courts in New York County
                            have exclusive jurisdiction.</p>
                        <p>
                            <strong>Consumer-protection notice:</strong> Nothing in these Terms
                            waives any right you may have under New York General Business Law
                            § 349 prohibiting deceptive acts and practices.
                        </p>
                    </div>

                    {/* ---------- 24. TERMINATION ---------- */}
                    <div className="legal__title text-subtitle">24. Termination</div>
                    <div className="legal__textbox">
                        You may discontinue use of the Platform at any time. Guidr may suspend or
                        terminate access immediately for violation of these Terms or to comply
                        with law. Sections 5–25 survive termination.
                    </div>

                    {/* ---------- 25. MISCELLANEOUS ---------- */}
                    <div className="legal__title text-subtitle">25. Miscellaneous</div>
                    <div className="legal__textbox">
                        These Terms constitute the entire agreement between you and Guidr. If any
                        provision is held unenforceable, the remaining provisions remain in full
                        force. No waiver is effective unless in writing. You may not assign your
                        rights or obligations; Guidr may assign freely. Headings are for
                        convenience only.
                    </div>

                    {/* ---------- CONTACT ---------- */}
                    <div className="legal__textbox">
                        Questions? Email <a href="mailto:info@guidr.net">info@guidr.net</a>
                    </div>

                </div>
            </div>
        );
    }
}
